/**
 * Canvas primitives extracted from CanvasStage: the pure,
 * component-free types, geometry / connector-routing math, style constants,
 * SVG/Mermaid exporters, and id generator. No React/Konva here - behaviour-
 * identical to the inline versions (verified by the full test suite).
 */

export type ShapeKind =
  | "rectangle" | "ellipse" | "diamond" | "cylinder"
  | "triangle" | "hexagon" | "parallelogram" | "text" | "sticky" | "image" | "draw" | "frame" | "table";

export type Routing = "straight" | "orthogonal" | "curved";
export interface El {
  id: string;
  kind: ShapeKind | "connector";
  x: number; y: number; w: number; h: number;
  stroke: string; fill: string; strokeWidth: number;
  text?: string;
  src?: string;                // image data URL (kind === "image")
  points?: number[];           // freehand/line polyline (kind === "draw"), relative to el.x/el.y
  closed?: boolean;            // draw polyline is closed (filled polygon)
  smooth?: boolean;            // freehand stroke → tension; line/arrow → sharp
  opacity?: number;            // 0..1 (imported shapes)
  fontSize?: number;           // text size in px (unset = the kind's default)
  fontFamily?: string;         // font KEY into FONTS ("sans"|"serif"|"mono"|"hand"); unset = sans
  align?: string;              // imported text align
  rows?: number;               // table row count (kind === "table")
  cols?: number;               // table column count (kind === "table")
  cells?: string[];            // table cell text, row-major, length rows*cols
  from?: string | null;        // connector source: bound shape id (null = free endpoint)
  to?: string | null;          // connector target: bound shape id (null = free endpoint)
  fromPoint?: Pt;              // free source coordinate (used when from is null)
  toPoint?: Pt;               // free target coordinate (used when to is null)
  waypoints?: Pt[];           // manual bend points (world coords)
  routing?: Routing;           // connector line routing
  dashed?: boolean;            // connector dashed
  dotted?: boolean;            // connector dotted
  startArrow?: boolean;        // connector arrowhead at the start (legacy on/off)
  endArrow?: boolean;          // connector arrowhead at the end (legacy on/off, default true)
  startHead?: string;          // start cap: none | arrow | circle | diamond
  endHead?: string;            // end cap: none | arrow | circle | diamond
  label?: string;              // connector label
  labelPos?: Pt;              // dragged label position (else path midpoint)
  fromSide?: string;          // pinned source side: top|right|bottom|left (fixed port)
  toSide?: string;            // pinned target side (fixed port)
  groupId?: string;           // members of the same group share this id (moved/selected together)
  groupName?: string;         // display name of the group (stored on every member)
  locked?: boolean;           // not marquee-selectable, not movable/resizable until unlocked (#29)
  hidden?: boolean;           // not rendered on the canvas and excluded from export while hidden (#29)
  rotation?: number;          // rotation in degrees about the element's top-left origin (#48)
}

export interface Comment { id: string; x: number; y: number; resolved: boolean; thread: any[]; }

export type Pt = { x: number; y: number };
export type Side = "left" | "right" | "top" | "bottom";
export const centerOf = (e: El) => ({ x: e.x + e.w / 2, y: e.y + e.h / 2 });
export const dist = (a: Pt, b: Pt) => Math.hypot(b.x - a.x, b.y - a.y);
// Typography. Elements store a font KEY, never a raw CSS stack: the stack is resolved here so
// there is one source of truth for canvas, HTML overlay and SVG export, and so an unknown or
// hostile value can never reach a style attribute (same reasoning as safeColor). These are
// web-safe stacks - no webfont fetch, so nothing to block, preload or fail offline.
export const FONTS: Record<string, string> = {
  sans: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  serif: "Georgia, Cambria, Times New Roman, serif",
  mono: "JetBrains Mono, Menlo, Consolas, monospace",
  hand: "Segoe Print, Bradley Hand, Chalkboard SE, Comic Sans MS, cursive",
};
export const DEFAULT_FONT = "sans";
/** CSS font stack for an element's font key; falls back to the default for unknown keys. */
export const fontStack = (key?: string): string => FONTS[key || ""] || FONTS[DEFAULT_FONT];
/** Text size for an element: its own fontSize when set, else the per-kind default. */
export const fontSizeOf = (el: El, fallback = 16): number =>
  el.fontSize || (el.kind === "sticky" ? 14 : el.kind === "table" ? 13 : fallback);

// Padding (px) around a shape within which a connector endpoint still binds to it (#61):
// aiming at the outline/edge of a box is the natural connect gesture, and a strict inside-only
// test made it silently fail and detach on the next move.
export const BIND_PAD = 8;
// Topmost non-connector shape whose box (expanded by `pad`) contains pt; later-drawn shapes win.
// pad=0 is the strict inside test used for selection; pad=BIND_PAD is the tolerant test used to
// bind connector endpoints, so releasing on or a few px outside a shape's outline attaches (#61).
export const shapeAtPoint = (shapes: El[], pt: Pt, pad = 0): El | undefined => {
  for (let i = shapes.length - 1; i >= 0; i--) {
    const e = shapes[i];
    if (e.kind === "connector") continue;
    if (pt.x >= e.x - pad && pt.x <= e.x + e.w + pad && pt.y >= e.y - pad && pt.y <= e.y + e.h + pad) return e;
  }
  return undefined;
};
// point on e's bounding box along the line toward `to`
export const anchor = (e: El, to: Pt): Pt => {
  const c = centerOf(e);
  const dx = to.x - c.x, dy = to.y - c.y;
  if (dx === 0 && dy === 0) return c;
  const s = 1 / Math.max(Math.abs(dx) / (e.w / 2), Math.abs(dy) / (e.h / 2));
  return { x: c.x + dx * s, y: c.y + dy * s };
};
// midpoint of the shape side facing `toward` (used for orthogonal / curved routing)
export const sideAnchor = (e: El, toward: Pt): Pt & { side: Side } => {
  const c = centerOf(e);
  const dx = toward.x - c.x, dy = toward.y - c.y;
  if (Math.abs(dx) * e.h >= Math.abs(dy) * e.w) {
    return dx >= 0 ? { x: e.x + e.w, y: c.y, side: "right" } : { x: e.x, y: c.y, side: "left" };
  }
  return dy >= 0 ? { x: c.x, y: e.y + e.h, side: "bottom" } : { x: c.x, y: e.y, side: "top" };
};
// anchor pinned to a specific side of a shape (fixed port), ignoring the target direction
export const fixedSideAnchor = (e: El, side: string): Pt & { side: Side } => {
  const c = centerOf(e);
  if (side === "right") return { x: e.x + e.w, y: c.y, side: "right" };
  if (side === "left") return { x: e.x, y: c.y, side: "left" };
  if (side === "bottom") return { x: c.x, y: e.y + e.h, side: "bottom" };
  return { x: c.x, y: e.y, side: "top" };
};
// which side of a shape a point is nearest to (for pinning a dropped endpoint)
export const nearestSide = (e: El, pt: Pt): Side => {
  const dl = pt.x - e.x, dr = e.x + e.w - pt.x, dt = pt.y - e.y, db = e.y + e.h - pt.y;
  const m = Math.min(dl, dr, dt, db);
  return m === dl ? "left" : m === dr ? "right" : m === dt ? "top" : "bottom";
};
export const isHoriz = (s: Side) => s === "left" || s === "right";
export const sideDir = (s: Side): Pt => (s === "right" ? { x: 1, y: 0 } : s === "left" ? { x: -1, y: 0 } : s === "bottom" ? { x: 0, y: 1 } : { x: 0, y: -1 });
// orthogonal elbow route between two side anchors
export const orthPoints = (sa: Pt & { side: Side }, ta: Pt & { side: Side }): Pt[] => {
  const stub = 18;
  const out = (p: Pt, s: Side): Pt => { const d = sideDir(s); return { x: p.x + d.x * stub, y: p.y + d.y * stub }; };
  const s1 = out(sa, sa.side), t1 = out(ta, ta.side);
  const pts: Pt[] = [sa, s1];
  if (isHoriz(sa.side) && isHoriz(ta.side)) {
    const mx = (s1.x + t1.x) / 2; pts.push({ x: mx, y: s1.y }, { x: mx, y: t1.y });
  } else if (!isHoriz(sa.side) && !isHoriz(ta.side)) {
    const my = (s1.y + t1.y) / 2; pts.push({ x: s1.x, y: my }, { x: t1.x, y: my });
  } else if (isHoriz(sa.side)) { pts.push({ x: t1.x, y: s1.y }); } else { pts.push({ x: s1.x, y: t1.y }); }
  pts.push(t1, ta);
  return pts;
};
// cubic bezier control points (curved routing)
export const curvePoints = (sa: Pt & { side: Side }, ta: Pt & { side: Side }): Pt[] => {
  const off = Math.max(40, dist(sa, ta) * 0.4);
  const d1 = sideDir(sa.side), d2 = sideDir(ta.side);
  return [sa, { x: sa.x + d1.x * off, y: sa.y + d1.y * off }, { x: ta.x + d2.x * off, y: ta.y + d2.y * off }, ta];
};
// point at half the arc length of a polyline (label anchor)
export const polyMid = (pts: Pt[]): Pt => {
  const segs: number[] = []; let total = 0;
  for (let i = 0; i < pts.length - 1; i++) { const d = dist(pts[i], pts[i + 1]); segs.push(d); total += d; }
  let acc = 0;
  for (let i = 0; i < segs.length; i++) {
    if (acc + segs[i] >= total / 2) { const t = segs[i] ? (total / 2 - acc) / segs[i] : 0; return { x: pts[i].x + (pts[i + 1].x - pts[i].x) * t, y: pts[i].y + (pts[i + 1].y - pts[i].y) * t }; }
    acc += segs[i];
  }
  return pts[0];
};
// turn a polyline into an orthogonal (right-angle) path (horizontal-first corners)
export const orthogonalize = (anchors: Pt[]): Pt[] => {
  const out: Pt[] = [anchors[0]];
  for (let i = 1; i < anchors.length; i++) {
    const a = out[out.length - 1], b = anchors[i];
    if (Math.abs(a.x - b.x) > 0.5 && Math.abs(a.y - b.y) > 0.5) out.push({ x: b.x, y: a.y });
    out.push(b);
  }
  return out;
};
// small gap kept between a bound endpoint and the shape edge (Excalidraw-style breathing room)
export const CONN_GAP = 5;
// shortest distance from point p to segment a-b (used to find which segment a line-grab lands on)
export const segDist = (p: Pt, a: Pt, b: Pt): number => {
  const dx = b.x - a.x, dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return dist(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return dist(p, { x: a.x + t * dx, y: a.y + t * dy });
};
export const norm = (v: Pt): Pt | null => { const m = Math.hypot(v.x, v.y); return m < 1e-9 ? null : { x: v.x / m, y: v.y / m }; };
export const segsOf = (pts: Pt[]): [Pt, Pt][] => { const s: [Pt, Pt][] = []; for (let i = 0; i < pts.length - 1; i++) s.push([pts[i], pts[i + 1]]); return s; };
// proper intersection point of segments ab & cd (excludes shared endpoints / near-touches), else null
export const properCross = (a: Pt, b: Pt, c: Pt, d: Pt): Pt | null => {
  const r = { x: b.x - a.x, y: b.y - a.y }, s = { x: d.x - c.x, y: d.y - c.y };
  const denom = r.x * s.y - r.y * s.x;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((c.x - a.x) * s.y - (c.y - a.y) * s.x) / denom;
  const u = ((c.x - a.x) * r.y - (c.y - a.y) * r.x) / denom;
  const e = 0.03;
  if (t <= e || t >= 1 - e || u <= e || u >= 1 - e) return null;
  return { x: a.x + t * r.x, y: a.y + t * r.y };
};
// "horizontalness" 0..1 of a segment (1 = horizontal); the more-horizontal line is the one that hops
export const hScore = (a: Pt, b: Pt): number => { const dx = Math.abs(b.x - a.x), dy = Math.abs(b.y - a.y); return dx / (dx + dy + 1e-9); };
// insert little semicircle "hops" into a polyline where it crosses OTHER polylines (line jumps)
export const withJumps = (pts: Pt[], others: Pt[][]): Pt[] => {
  if (pts.length < 2 || others.length === 0) return pts;
  const R = 5, out: Pt[] = [pts[0]];
  for (let i = 0; i < pts.length - 1; i++) {
    const A = pts[i], B = pts[i + 1];
    const u = norm({ x: B.x - A.x, y: B.y - A.y });
    if (!u) { out.push(B); continue; }
    const myH = hScore(A, B), n = { x: u.y, y: -u.x };
    const cross: number[] = [];
    for (const other of others) for (const [c, d] of segsOf(other)) {
      const p = properCross(A, B, c, d);
      if (p && myH > hScore(c, d) + 0.01) cross.push(Math.hypot(p.x - A.x, p.y - A.y));
    }
    cross.sort((x, y) => x - y);
    let prevD = 0;
    for (const cd of cross) {
      if (cd - R < prevD + 1 || cd + R > Math.hypot(B.x - A.x, B.y - A.y)) continue;
      out.push({ x: A.x + u.x * (cd - R), y: A.y + u.y * (cd - R) });
      const N = 6, cx = A.x + u.x * cd, cy = A.y + u.y * cd;
      for (let k = 1; k < N; k++) { const th = Math.PI * (1 - k / N); out.push({ x: cx + R * Math.cos(th) * u.x + R * Math.sin(th) * n.x, y: cy + R * Math.cos(th) * u.y + R * Math.sin(th) * n.y }); }
      out.push({ x: A.x + u.x * (cd + R), y: A.y + u.y * (cd + R) });
      prevD = cd + R;
    }
    out.push(B);
  }
  return out;
};
// drop duplicate + collinear points from an orthogonal polyline
export const simplifyPath = (pts: Pt[]): Pt[] => {
  const d: Pt[] = [];
  for (const p of pts) { const q = d[d.length - 1]; if (q && Math.abs(q.x - p.x) < 0.5 && Math.abs(q.y - p.y) < 0.5) continue; d.push(p); }
  if (d.length <= 2) return d;
  const res: Pt[] = [d[0]];
  for (let i = 1; i < d.length - 1; i++) {
    const a = res[res.length - 1], b = d[i], c = d[i + 1];
    const col = (Math.abs(a.x - b.x) < 0.5 && Math.abs(b.x - c.x) < 0.5) || (Math.abs(a.y - b.y) < 0.5 && Math.abs(b.y - c.y) < 0.5);
    if (!col) res.push(b);
  }
  res.push(d[d.length - 1]);
  return res;
};
// orthogonal A* over a Hanan grid (shape edges ± margin) → a route from a to b avoiding obstacle rects.
// Returns the polyline (incl a & b) or null if no route found. Turn-penalized so it prefers few bends.
export const avoidRoute = (a: Pt, b: Pt, obstacles: { x: number; y: number; w: number; h: number }[]): Pt[] | null => {
  const M = 14;
  const rects = obstacles.map((o) => ({ x: o.x - M, y: o.y - M, r: o.x + o.w + M, b: o.y + o.h + M }));
  const inRect = (x: number, y: number) => rects.some((R) => x > R.x + 0.5 && x < R.r - 0.5 && y > R.y + 0.5 && y < R.b - 0.5);
  const xsSet = new Set<number>([a.x, b.x, (a.x + b.x) / 2]);
  const ysSet = new Set<number>([a.y, b.y, (a.y + b.y) / 2]);
  for (const R of rects) { xsSet.add(R.x); xsSet.add(R.r); ysSet.add(R.y); ysSet.add(R.b); }
  const xs = [...xsSet].sort((p, q) => p - q), ys = [...ysSet].sort((p, q) => p - q);
  if (xs.length * ys.length > 4000) return null; // guard: too dense, bail to simple routing
  const ai = xs.indexOf(a.x), aj = ys.indexOf(a.y), bi = xs.indexOf(b.x), bj = ys.indexOf(b.y);
  const key = (i: number, j: number) => i * 100000 + j;
  const segOpen = (x1: number, y1: number, x2: number, y2: number) => {
    const steps = 10;
    for (let k = 1; k < steps; k++) { if (inRect(x1 + (x2 - x1) * k / steps, y1 + (y2 - y1) * k / steps)) return false; }
    return true;
  };
  const start = key(ai, aj), goal = key(bi, bj);
  const g = new Map<number, number>([[start, 0]]), came = new Map<number, number>(), dir = new Map<number, string>();
  const open = new Map<number, number>([[start, Math.abs(a.x - b.x) + Math.abs(a.y - b.y)]]);
  while (open.size) {
    let cur = -1, cf = Infinity;
    for (const [k, f] of open) if (f < cf) { cf = f; cur = k; }
    open.delete(cur);
    if (cur === goal) break;
    const ci = Math.floor(cur / 100000), cj = cur % 100000;
    const neigh: [number, number][] = [];
    if (ci + 1 < xs.length) neigh.push([ci + 1, cj]); if (ci - 1 >= 0) neigh.push([ci - 1, cj]);
    if (cj + 1 < ys.length) neigh.push([ci, cj + 1]); if (cj - 1 >= 0) neigh.push([ci, cj - 1]);
    for (const [ni, nj] of neigh) {
      if (!segOpen(xs[ci], ys[cj], xs[ni], ys[nj])) continue;
      const nk = key(ni, nj), moveDir = ni !== ci ? "h" : "v";
      const turn = dir.get(cur) && dir.get(cur) !== moveDir ? 24 : 0;
      const tentative = (g.get(cur) ?? Infinity) + Math.abs(xs[ni] - xs[ci]) + Math.abs(ys[nj] - ys[cj]) + turn;
      if (tentative < (g.get(nk) ?? Infinity)) {
        came.set(nk, cur); g.set(nk, tentative); dir.set(nk, moveDir);
        open.set(nk, tentative + Math.abs(xs[ni] - b.x) + Math.abs(ys[nj] - b.y));
      }
    }
  }
  if (start !== goal && !came.has(goal)) return null;
  const path = [goal]; let ck = goal;
  while (ck !== start && came.has(ck)) { ck = came.get(ck)!; path.unshift(ck); }
  if (path[0] !== start) return null;
  return simplifyPath(path.map((k) => ({ x: xs[Math.floor(k / 100000)], y: ys[k % 100000] })));
};
// intersect ray (o + t·dir, t ≥ 0) with segment a-b; returns the nearest forward hit or null
export const raySeg = (o: Pt, dir: Pt, a: Pt, b: Pt): { t: number; pt: Pt } | null => {
  const sx = b.x - a.x, sy = b.y - a.y;
  const denom = dir.y * sx - dir.x * sy;
  if (Math.abs(denom) < 1e-9) return null;
  const ax = a.x - o.x, ay = a.y - o.y;
  const t = (sx * ay - sy * ax) / denom;      // along the ray
  const u = (dir.x * ay - dir.y * ax) / denom; // along the segment
  if (t < 0 || u < -1e-6 || u > 1 + 1e-6) return null;
  return { t, pt: { x: o.x + dir.x * t, y: o.y + dir.y * t } };
};
// The point where the center→`toward` ray exits the shape's REAL outline - the ellipse
// curve, a polygon edge, or the box edge - NOT a snap to one of four mid-edge anchors. For
// a diagonal connection this is where the line actually meets the shape, so the arrow reads
// as attached instead of floating ~50px away at a fixed top/side anchor (#42).
export const outlineAnchor = (e: El, toward: Pt): Pt => {
  const c = centerOf(e);
  const dx = toward.x - c.x, dy = toward.y - c.y;
  const rx = Math.max(0.5, e.w / 2), ry = Math.max(0.5, e.h / 2);
  if (dx === 0 && dy === 0) return c;
  if (e.kind === "ellipse" || e.kind === "cylinder") {
    // the ray meets the ellipse (X/rx)²+(Y/ry)²=1 at t = 1/√((dx/rx)²+(dy/ry)²)
    const t = 1 / Math.hypot(dx / rx, dy / ry);
    if (isFinite(t)) return { x: c.x + dx * t, y: c.y + dy * t };
  }
  const poly = shapePoly(e);   // triangle / diamond / hexagon / parallelogram (local coords)
  if (poly) {
    let best: Pt | null = null, bestT = Infinity;
    for (let i = 0; i < poly.length; i++) {
      const a = { x: e.x + poly[i][0], y: e.y + poly[i][1] };
      const b = { x: e.x + poly[(i + 1) % poly.length][0], y: e.y + poly[(i + 1) % poly.length][1] };
      const hit = raySeg(c, { x: dx, y: dy }, a, b);
      if (hit && hit.t > 1e-6 && hit.t < bestT) { bestT = hit.t; best = hit.pt; }
    }
    if (best) return best;
  }
  // rectangle / sticky / frame / table / image / text → bounding-box intersection
  const s = 1 / Math.max(Math.abs(dx) / rx, Math.abs(dy) / ry);
  return { x: c.x + dx * s, y: c.y + dy * s };
};
// full geometry for a connector: resolves bound/free endpoints, waypoints and routing.
// Returns null if a bound endpoint's shape is gone (orphan). `anchors` = [src, ...waypoints, dst]
// (the logical points, used for handles); `pts` = the rendered polyline.
export const connectorPath = (c: El, els: El[]) => {
  const fromShape = c.from ? els.find((e) => e.id === c.from) : null;
  const toShape = c.to ? els.find((e) => e.id === c.to) : null;
  if ((c.from && !fromShape) || (c.to && !toShape)) return null;
  const wp = c.waypoints || [];
  const routing = c.routing || "straight";
  const zero = { x: 0, y: 0 };
  const srcToward = wp[0] || (toShape ? centerOf(toShape) : c.toPoint) || zero;
  const dstToward = wp[wp.length - 1] || (fromShape ? centerOf(fromShape) : c.fromPoint) || zero;
  let srcSide = fromShape ? (c.fromSide ? fixedSideAnchor(fromShape, c.fromSide) : sideAnchor(fromShape, srcToward)) : null;
  let dstSide = toShape ? (c.toSide ? fixedSideAnchor(toShape, c.toSide) : sideAnchor(toShape, dstToward)) : null;
  // side anchors drive the elbow / curved / smart routers (which must leave a shape
  // perpendicular to one of its four sides); pull them off along the outward normal for a gap.
  if (srcSide) { const d = sideDir(srcSide.side); srcSide = { ...srcSide, x: srcSide.x + d.x * CONN_GAP, y: srcSide.y + d.y * CONN_GAP }; }
  if (dstSide) { const d = sideDir(dstSide.side); dstSide = { ...dstSide, x: dstSide.x + d.x * CONN_GAP, y: dstSide.y + d.y * CONN_GAP }; }
  // a STRAIGHT connector attaches at the true outline intersection along the line it travels,
  // with a small uniform gap in that same direction - not at a fixed mid-edge anchor (#42).
  const gapAlong = (p: Pt, toward: Pt): Pt => { const u = norm({ x: toward.x - p.x, y: toward.y - p.y }); return u ? { x: p.x + u.x * CONN_GAP, y: p.y + u.y * CONN_GAP } : p; };
  const useOutline = routing === "straight";
  const src: Pt = useOutline
    ? (fromShape ? gapAlong(outlineAnchor(fromShape, srcToward), srcToward) : (c.fromPoint || zero))
    : (srcSide ? { x: srcSide.x, y: srcSide.y } : (c.fromPoint || zero));
  const dst: Pt = useOutline
    ? (toShape ? gapAlong(outlineAnchor(toShape, dstToward), dstToward) : (c.toPoint || zero))
    : (dstSide ? { x: dstSide.x, y: dstSide.y } : (c.toPoint || zero));
  const anchors: Pt[] = [src, ...wp, dst];
  let pts = anchors, bezier = false, tension = 0;
  if (wp.length === 0) {
    if (routing === "straight") pts = [src, dst];
    else if (routing === "curved" && srcSide && dstSide) { pts = curvePoints(srcSide, dstSide); bezier = true; }
    else if (routing === "curved") { pts = [src, dst]; tension = 0.5; }
    else if (routing === "avoid" && srcSide && dstSide) {
      // route orthogonally AROUND other shapes, entering/leaving each shape along its side's
      // normal (stub out first) so the final segment matches the side it lands on (#42).
      const obst = els.filter((e) => e.kind !== "connector" && e.id !== c.from && e.id !== c.to).map((e) => ({ x: e.x, y: e.y, w: e.w, h: e.h }));
      const sd = sideDir(srcSide.side), td = sideDir(dstSide.side);
      const s1 = { x: srcSide.x + sd.x * 18, y: srcSide.y + sd.y * 18 };
      const t1 = { x: dstSide.x + td.x * 18, y: dstSide.y + td.y * 18 };
      const mid = avoidRoute(s1, t1, obst);
      pts = mid ? simplifyPath([src, ...mid, dst]) : orthPoints(srcSide, dstSide);
    }
    else if (routing === "orthogonal" && srcSide && dstSide) pts = orthPoints(srcSide, dstSide);
    else pts = orthogonalize(anchors);
  } else if (routing === "curved") { pts = anchors; tension = 0.45; }
  else if (routing === "orthogonal") pts = orthogonalize(anchors);
  else pts = anchors;
  return { pts, anchors, src, dst, bezier, tension };
};

export const INK = "#4f46e5";
export const FILL = "#eef2ff";
export const NOTE = "#fef9c3";
// The stroke palette offered for EVERY element - shapes and connectors alike - so the canvas
// speaks one visual language instead of giving arrows their own private set of colours.
// What a touch contact should do. Palm rejection lives here as pure policy so it can be
// reasoned about (and tested) without a tablet: while a stylus is on the glass - or was a
// moment ago - finger contacts do NOTHING, because on an iPad the hand resting on the screen
// is a finger contact and would otherwise draw, select, or start a pinch while you write.
export type TouchIntent = "draw" | "pinch" | "ignore";
export const PEN_GRACE_MS = 1200;
export const touchIntent = (o: { stylus: boolean; touches: number; penRecent: boolean }): TouchIntent => {
  if (o.stylus) return "draw";        // the pen always wins, whatever else is on the glass
  if (o.penRecent) return "ignore";   // a palm/finger while writing: reject it
  return o.touches >= 2 ? "pinch" : "draw";
};

export const STROKE_SWATCHES = [INK, "#0ea5e9", "#059669", "#dc2626", "#475569", "#1f2430"];
// Device-mirror key for one page, scoped by diagram so two diagrams that both have a
// page "p1" don't collide (which leaked one diagram's content into another).
export const storeKeyFor = (id?: string, docId?: string) => `libredraw:page:${docId || "default"}:${id || "default"}`;
export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
export const GRID = 10;
export const snap = (v: number) => Math.round(v / GRID) * GRID;
export const snapPt = (p: Pt): Pt => ({ x: snap(p.x), y: snap(p.y) });
// ---- vector / text export (pure functions of the element list) ----
export const xesc = (s: any) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
export const polyStr = (pts: Pt[]) => pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
export const shapePoly = (el: El): [number, number][] | null => {
  const w = el.w, h = el.h;
  if (el.kind === "triangle") return [[w / 2, 0], [w, h], [0, h]];
  if (el.kind === "hexagon") return [[w * 0.25, 0], [w * 0.75, 0], [w, h / 2], [w * 0.75, h], [w * 0.25, h], [0, h / 2]];
  if (el.kind === "parallelogram") return [[w * 0.25, 0], [w, 0], [w * 0.75, h], [0, h]];
  if (el.kind === "diamond") return [[w / 2, 0], [w, h / 2], [w / 2, h], [0, h / 2]];
  return null;
};
// Path-based shapes that aren't simple polygons: a flowchart document (wavy bottom), a UML class
// (name-compartment divider), a UML package (folder tab), a 3D cube (compute/server/node), and a
// cloud. Returns an SVG path `d` in LOCAL w×h coordinates - one definition, rendered on the canvas
// via a Konva <Path>, exported as an SVG <path>, and mirrored by the palette preview, so a palette
// shape looks the same everywhere it appears (fixes shapes inserting as a plain rectangle).
export const PATH_KINDS = new Set(["document", "class", "package", "cube", "cloud", "firewall"]);
export const shapePath = (kind: string, w: number, h: number): string | null => {
  const r = (n: number) => Math.round(n * 100) / 100;
  if (kind === "firewall") {
    // a brick wall: outer box + three courses, with staggered vertical joints (network firewall)
    const q1 = r(h * 0.25), q2 = r(h * 0.5), q3 = r(h * 0.75);
    const a = r(w / 3), b = r(2 * w / 3), c = r(w / 6), d = r(w / 2), e = r(5 * w / 6);
    return `M0 0 H${r(w)} V${r(h)} H0 Z M0 ${q1} H${r(w)} M0 ${q2} H${r(w)} M0 ${q3} H${r(w)}`
      + ` M${a} 0 V${q1} M${b} 0 V${q1} M${c} ${q1} V${q2} M${d} ${q1} V${q2} M${e} ${q1} V${q2}`
      + ` M${a} ${q2} V${q3} M${b} ${q2} V${q3} M${c} ${q3} V${r(h)} M${d} ${q3} V${r(h)} M${e} ${q3} V${r(h)}`;
  }
  if (kind === "document") return `M0 0 H${r(w)} V${r(h * 0.82)} C${r(w * 0.66)} ${r(h * 1.04)} ${r(w * 0.34)} ${r(h * 0.6)} 0 ${r(h * 0.82)} Z`;
  if (kind === "class") return `M0 0 H${r(w)} V${r(h)} H0 Z M0 ${r(h * 0.28)} H${r(w)}`;
  if (kind === "package") { const t = r(h * 0.18); return `M0 ${t} V0 H${r(w * 0.42)} V${t} H${r(w)} V${r(h)} H0 Z`; }
  if (kind === "cube") { const d = r(Math.min(w, h) * 0.22); return `M0 ${d} L${d} 0 H${r(w)} V${r(h - d)} L${r(w - d)} ${r(h)} H0 Z M0 ${d} H${r(w - d)} V${r(h)} M${r(w - d)} ${d} L${r(w)} 0`; }
  if (kind === "cloud") return `M${r(w * 0.25)} ${r(h * 0.9)} C${r(w * 0.04)} ${r(h * 0.9)} ${r(w * 0.02)} ${r(h * 0.52)} ${r(w * 0.2)} ${r(h * 0.48)} C${r(w * 0.16)} ${r(h * 0.16)} ${r(w * 0.52)} ${r(h * 0.08)} ${r(w * 0.57)} ${r(h * 0.34)} C${r(w * 0.72)} ${r(h * 0.12)} ${r(w * 0.98)} ${r(h * 0.28)} ${r(w * 0.82)} ${r(h * 0.5)} C${r(w)} ${r(h * 0.54)} ${r(w * 0.96)} ${r(h * 0.9)} ${r(w * 0.74)} ${r(h * 0.9)} Z`;
  return null;
};
// Only allow hex / named / rgb(a) colors into SVG attributes. `fill`/`stroke` come from
// element data that can be imported from a .excalidrawlib, so an attribute-breakout string
// like `" onload="alert(1)` must not reach the serialized SVG. Text
// content is already escaped via xesc(); this guards the color attributes it doesn't cover.
export const safeColor = (c?: string): string =>
  c && /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]+|rgba?\([\d.,%\s]+\))$/.test(c.trim()) ? c.trim() : "#000000";

export const elsToSvg = (allEls: El[]): string => {
  const els = allEls.filter((e) => !e.hidden);  // hidden objects are excluded from export (#29)
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const bump = (x: number, y: number) => { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); };
  for (const e of els) { if (e.kind === "connector") continue; bump(e.x, e.y); bump(e.x + e.w, e.y + e.h); }
  for (const c of els.filter((e) => e.kind === "connector")) { const p = connectorPath(c, els); if (p) p.pts.forEach((pt) => bump(pt.x, pt.y)); }
  if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 10; maxY = 10; }
  const pad = 24, W = maxX - minX + pad * 2, H = maxY - minY + pad * 2, ox = -minX + pad, oy = -minY + pad;
  const parts: string[] = [`<defs><marker id="a" markerWidth="10" markerHeight="8" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6 Z" fill="#64748b"/></marker></defs>`];
  for (const c of els.filter((e) => e.kind === "connector")) {
    const p = connectorPath(c, els); if (!p) continue;
    const stroke = safeColor(c.stroke || "#64748b"), sw = c.strokeWidth || 2;
    const dash = c.dotted ? ' stroke-dasharray="2 6"' : c.dashed ? ' stroke-dasharray="9 7"' : "";
    const mk = (c.endHead || (c.endArrow !== false ? "arrow" : "none")) === "arrow" ? ' marker-end="url(#a)"' : "";
    parts.push(`<polyline points="${polyStr(p.pts.map((pt) => ({ x: pt.x + ox, y: pt.y + oy })))}" fill="none" stroke="${stroke}" stroke-width="${sw}"${dash} stroke-linejoin="round" stroke-linecap="round"${mk}/>`);
    if (c.label) { const m = polyMid(p.pts); parts.push(`<text x="${(m.x + ox).toFixed(1)}" y="${(m.y + oy).toFixed(1)}" font-family="${xesc(fontStack(c.fontFamily))}" font-size="12" fill="#334155" text-anchor="middle">${xesc(c.label)}</text>`); }
  }
  for (const el of els.filter((e) => e.kind !== "connector")) {
    const start = parts.length;   // #48: wrap this element's parts in a rotate transform below
    const x = el.x + ox, y = el.y + oy, fill = el.fill === "transparent" ? "none" : safeColor(el.fill), stroke = safeColor(el.stroke), sw = el.strokeWidth || 2;
    if (el.kind === "rectangle" || el.kind === "sticky") parts.push(`<rect x="${x}" y="${y}" width="${el.w}" height="${el.h}" rx="${el.kind === "sticky" ? 3 : 6}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`);
    else if (el.kind === "ellipse") parts.push(`<ellipse cx="${x + el.w / 2}" cy="${y + el.h / 2}" rx="${el.w / 2}" ry="${el.h / 2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`);
    else if (el.kind === "cylinder") { const ry = Math.min(el.h * 0.16, 16); parts.push(`<rect x="${x}" y="${y + ry}" width="${el.w}" height="${el.h - ry * 2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/><ellipse cx="${x + el.w / 2}" cy="${y + el.h - ry}" rx="${el.w / 2}" ry="${ry}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/><ellipse cx="${x + el.w / 2}" cy="${y + ry}" rx="${el.w / 2}" ry="${ry}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`); }
    else if (el.kind === "image" && el.src) parts.push(`<image x="${x}" y="${y}" width="${el.w}" height="${el.h}" href="${el.src}"/>`);
    else if (el.kind === "draw" && el.points && el.points.length > 1) {
      const pts: Pt[] = []; for (let i = 0; i + 1 < el.points.length; i += 2) pts.push({ x: x + el.points[i], y: y + el.points[i + 1] });
      const tag = el.closed ? "polygon" : "polyline";
      const op = el.opacity ?? 1;
      parts.push(`<${tag} points="${polyStr(pts)}" fill="${el.closed ? fill : "none"}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round"${op < 1 ? ` opacity="${op}"` : ""}/>`);
    }
    else if (PATH_KINDS.has(el.kind)) { const d = shapePath(el.kind, el.w, el.h); if (d) parts.push(`<path transform="translate(${x} ${y})" d="${d}" fill="${el.fill === "transparent" ? "none" : fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round"/>`); }
    else if (el.kind !== "text") { const poly = shapePoly(el); if (poly) parts.push(`<polygon points="${polyStr(poly.map(([px, py]) => ({ x: px + x, y: py + y })))}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round"/>`); }
    if (el.text && el.kind !== "image") {
      if (el.fontSize) { // imported text: own size / colour / alignment
        const anchor = el.align === "center" ? "middle" : el.align === "right" ? "end" : "start";
        const ax = el.align === "center" ? x + el.w / 2 : el.align === "right" ? x + el.w : x;
        parts.push(`<text x="${ax}" y="${y + (el.h || el.fontSize) * 0.72}" font-family="${xesc(fontStack(el.fontFamily))}" font-weight="600" font-size="${el.fontSize}" fill="${stroke}" text-anchor="${anchor}">${xesc(el.text)}</text>`);
      } else { const px = el.kind === "sticky" ? 10 : 6; parts.push(`<text x="${x + px}" y="${y + (el.kind === "sticky" ? 24 : 20)}" font-family="${xesc(fontStack(el.fontFamily))}" font-size="${fontSizeOf(el)}" fill="#1f2430">${xesc(el.text)}</text>`); }
    }
    // #48: rotate the element's shapes+label as a unit, about its top-left origin - the same
    // point Konva rotates the element's group about on the canvas (the Transformer moved x/y to
    // pivot visually about the centre, so top-left rotation here reproduces the canvas exactly).
    if (el.rotation) { const inner = parts.splice(start).join(""); parts.push(`<g transform="rotate(${el.rotation.toFixed(2)} ${x.toFixed(1)} ${y.toFixed(1)})">${inner}</g>`); }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W.toFixed(0)}" height="${H.toFixed(0)}" viewBox="0 0 ${W.toFixed(0)} ${H.toFixed(0)}"><rect width="100%" height="100%" fill="#ffffff"/>${parts.join("")}</svg>`;
};
const MERMAID_NON_NODE = new Set(["connector", "frame", "table"]);
export const elsToMermaid = (allEls: El[]): string => {
  const els = allEls.filter((e) => !e.hidden);  // hidden objects are excluded from export (#29)
  // Frames (containers) and tables (data grids) are not flowchart nodes. Emitting them
  // produced a spurious `n1["Frame"]` (the frame's own chrome word) and an empty `n2[""]`
  // for a table - a diagram the user never drew. They're excluded here; a connector that
  // references an excluded element is dropped by the id lookup in the edge loop below (#38).
  const shapes = els.filter((e) => !MERMAID_NON_NODE.has(e.kind));
  // Entity-encode every character that Mermaid would otherwise read as markup or as a
  // node/label delimiter, so the label renders as the literal canvas text (e.g.
  // `Start <b>QA</b>` stays literal, `a [b] (c) {d}` keeps its brackets). `#` first so our
  // own entities below aren't re-encoded. Covers the full set the shape-wrapping syntax
  // uses - [] () {} for rect/round/diamond, | for edge labels - plus quotes/angle brackets.
  const esc = (t: string) =>
    t.replace(/#/g, "#35;")
      .replace(/"/g, "#quot;")
      .replace(/</g, "#lt;")
      .replace(/>/g, "#gt;")
      .replace(/\[/g, "#91;")
      .replace(/\]/g, "#93;")
      .replace(/\(/g, "#40;")
      .replace(/\)/g, "#41;")
      .replace(/\{/g, "#123;")
      .replace(/\}/g, "#125;")
      .replace(/\|/g, "#124;")
      .replace(/\r?\n/g, "#10;");
  // A shape's own text only - never its kind. An untitled shape gets an empty label
  // rather than the fabricated word "rectangle"/"ellipse".
  const lbl = (s: El) => esc((s.text || "").replace(/\s+/g, " ").trim());
  const wrapNode = (kind: string, t: string) => { const q = `"${t}"`;
    if (kind === "diamond") return `{${q}}`;
    if (kind === "ellipse") return `((${q}))`;
    if (kind === "cylinder") return `[(${q})]`;
    if (kind === "hexagon") return `{{${q}}}`;
    if (kind === "parallelogram") return `[/${q}/]`;
    return `[${q}]`;
  };
  // Collapse a placed library icon - a group of a box + caption + decorations sharing a groupId -
  // into ONE flowchart node. Emitting each member as its own node turned a single icon into a
  // cluster of boxes (#47). A member id maps to its group's node, so a connector attached to any
  // part of the icon points at the one node. Ungrouped shapes stay one node each.
  const idMap = new Map<string, string>();
  const nodes: { nid: string; kind: string; label: string; gname: string }[] = [];
  const groupIdx = new Map<string, number>();
  let counter = 0;
  for (const s of shapes) {
    if (s.groupId) {
      let idx = groupIdx.get(s.groupId);
      if (idx === undefined) {
        idx = nodes.length; groupIdx.set(s.groupId, idx);
        const gn = (s.groupName && s.groupName !== "Group") ? esc(String(s.groupName).replace(/\s+/g, " ").trim()) : "";
        nodes.push({ nid: `n${counter++}`, kind: "rectangle", label: "", gname: gn });
      }
      const n = nodes[idx];
      idMap.set(s.id, n.nid);
      if (!n.label && lbl(s)) n.label = lbl(s);   // the caption member's text becomes the node label
      // a collapsed icon renders as a plain box node (its members have mixed kinds; a box is the
      // predictable, neutral choice - we don't guess a flowchart shape from a decoration).
    } else {
      idMap.set(s.id, `n${counter}`);
      nodes.push({ nid: `n${counter++}`, kind: s.kind, label: lbl(s), gname: "" });
    }
  }
  const lines = ["flowchart TD"];
  // A node WITH text → a labelled, shape-wrapped node; a grouped icon uses its caption, falling
  // back to its authored group name if no member carries text. A node with NO text → a bare id
  // (a plain box): never the fabricated word "rectangle" (#6), never an anonymous `n0[""]` (#38).
  for (const n of nodes) { const t = n.label || n.gname; lines.push(t ? `  ${n.nid}${wrapNode(n.kind, t)}` : `  ${n.nid}`); }
  for (const c of els.filter((e) => e.kind === "connector")) {
    if (!c.from || !c.to) continue;
    // a === b: both endpoints resolved to the SAME node (an intra-icon connector inside one
    // grouped library item) - skip it rather than emit a self-loop on the collapsed node.
    const a = idMap.get(c.from), b = idMap.get(c.to); if (!a || !b || a === b) continue;
    const arrow = (c.endHead || "arrow") === "none" && (c.startHead || "none") === "none" ? "---" : "-->";
    // esc() already encodes the `|` edge-label delimiter, so a connector caption can't break the line
    const clabel = c.label ? esc(String(c.label).replace(/\s+/g, " ").trim()) : "";
    lines.push(clabel ? `  ${a} ${arrow}|"${clabel}"| ${b}` : `  ${a} ${arrow} ${b}`);
  }
  return lines.join("\n");
};
export let seq = 0;
export const uid = () => `el_${(seq++).toString(36)}_${Math.floor(performance.now()).toString(36)}`;

// The jac client build only bundles helper modules referenced from a .cl.jac import
// (a .tsx like CanvasStage can't trigger the copy of a local module). EditorShell imports
// this marker so the canvas engine's geometry module reaches the build dir.
export const CANVAS_GEOMETRY_MODULE = true;
